import React from "react";
import "./qualification.css";
import { useReveal } from "../../lib/useReveal";
import { certifications, credlyProfiles, education, experience } from "../../data/site";

/* ---------- plain lists: used on tablets and phones ---------- */
const Timeline = ({ title, items, footer }) => (
  <section className="timeline" aria-labelledby={`tl-${title}`}>
    <h3 id={`tl-${title}`} className="timeline__title">
      {title}
    </h3>
    <ol className="timeline__list">
      {items.map((item) => (
        <li key={`${item.title}-${item.place}`} className="timeline__item">
          <p className="timeline__role">{item.title}</p>
          <p className="timeline__place">{item.place}</p>
          <p className="timeline__when">
            {item.when}
            {item.url && (
              <>
                {" · "}
                <a href={item.url} target="_blank" rel="noreferrer">
                  {item.linkLabel ?? "Verify"}
                  <span className="sr-only"> for {item.title} (opens in a new tab)</span>
                </a>
              </>
            )}
          </p>
        </li>
      ))}
    </ol>
    {footer}
  </section>
);

const BadgeLinks = () => (
  <p className="timeline__more">
    All badges on Credly:{" "}
    {credlyProfiles.map((p, i) => (
      <React.Fragment key={p.url}>
        {i > 0 && " · "}
        <a href={p.url} target="_blank" rel="noreferrer">
          {p.label}
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </React.Fragment>
    ))}
  </p>
);

/* ---------- career chart: used on larger screens ---------- */
const START = 2019;
const END = 2027;
const years = Array.from({ length: END - START }, (_, i) => START + i);
const frac = (v) => Math.min(Math.max((v - START) / (END - START), 0), 1);

const now = (() => {
  const d = new Date();
  return d.getFullYear() + d.getMonth() / 12 + d.getDate() / 372;
})();

const Lane = ({ title, kind, items, footer }) => {
  // oldest first, so each lane reads top to bottom in time order
  const rows = [...items].sort((a, b) => (a.from ?? a.at) - (b.from ?? b.at));
  return (
    <section className={`chart__lane chart__lane--${kind}`} aria-labelledby={`ch-${kind}`}>
      <h3 id={`ch-${kind}`} className="chart__lname">
        {title}
      </h3>
      <div className="chart__plot">
        <ol className="chart__rows">
          {rows.map((item, i) => {
            const isPoint = item.at != null;
            const from = isPoint ? item.at : item.from;
            const to = isPoint ? item.at : item.to === "now" ? now : item.to;
            const a = frac(from);
            const b = frac(to);
            // wide bars hold their own label; otherwise it sits past the item, flipping left near the right edge
            const inside = !isPoint && b - a >= 0.4;
            const flip = !inside && b > 0.62;
            // education: one box that holds its own text (it grows past the true date span only if the text needs more room)
            if (kind === "edu") {
              return (
                <li key={`${item.title}-${item.place}`} className="chart__row" style={{ "--a": a, "--b": b, "--i": i }}>
                  <span className={`chart__box${b > 0.62 ? " chart__box--end" : ""}`}>
                    <strong>{item.title}</strong>
                    <span className="chart__place">{item.place}</span>
                    <span className="chart__when">{item.when}</span>
                  </span>
                </li>
              );
            }
            return (
              <li key={`${item.title}-${item.place}`} className="chart__row" style={{ "--a": a, "--b": b, "--i": i }}>
                <span className={isPoint ? "chart__dot" : "chart__bar"} aria-hidden="true" />
                <span className={`chart__label${inside ? " chart__label--in" : flip ? " chart__label--left" : ""}`}>
                  <strong>{item.title}</strong>
                  <span className="chart__place">{item.place}</span>
                  <span className="chart__when">
                    {item.when}
                    {item.url && (
                      <>
                        {" · "}
                        <a href={item.url} target="_blank" rel="noreferrer">
                          Verify
                          <span className="sr-only"> for {item.title} (opens in a new tab)</span>
                        </a>
                      </>
                    )}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
        {footer}
      </div>
    </section>
  );
};

const Chart = () => {
  const [ref, shown] = useReveal({ threshold: 0.2 });
  return (
    <div className="chart" ref={ref} data-shown={shown} style={{ "--now": frac(now) }}>
      <div className="chart__axis" aria-hidden="true">
        {years.map((y) => (
          <span key={y} style={{ "--a": frac(y) }}>
            {y}
          </span>
        ))}
        <span className="chart__now" style={{ "--a": frac(now) }}>
          now
        </span>
      </div>
      <Lane title="Education" kind="edu" items={education} />
      <Lane title="Experience" kind="exp" items={experience} />
      <Lane title="Certifications" kind="cert" items={certifications} footer={<BadgeLinks />} />
    </div>
  );
};

/** Wide screens (1200px and up) get the career chart; everything narrower gets the plain lists (one is always hidden). */
const Qualification = () => (
  <>
    <Chart />
    <div className="qualification">
      <Timeline title="Education" items={education} />
      <Timeline title="Experience" items={experience} />
      <Timeline title="Certifications" items={certifications} footer={<BadgeLinks />} />
    </div>
  </>
);

export default Qualification;
